import { useMemo, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { simulationApi } from '@/events/simulationEngine';
import DashboardKPITile from '@/components/DashboardKPITile';
import { aud, simTimeShort } from '@/utils/format';
import {
  Search,
  MapPin,
  Brush,
  Wallet,
  Calendar,
  Sparkles,
  PlayCircle,
  PauseCircle,
  Phone,
} from 'lucide-react';
import type { DesignType, EngagementStatus, TailRecord } from '@/domain/types';
import clsx from 'clsx';

type RecencyBand = 'any' | '0-30' | '31-90' | '91-180' | '181-365' | '1-2y' | '2y+';
type Radius = 'any' | '10' | '25' | '50' | '100';

interface Filters {
  recency: RecencyBand;
  radiusKm: Radius;
  anchorStudio: string;
  artistId: string | 'any';
  designType: DesignType | 'unfinished-sleeves' | 'any';
  depositHistory: 'any' | 'paid-ever' | 'never' | 'multi' | 'has-credit';
  signal: 'any' | 'future-readiness' | 'lapsed' | 'no-signal';
  engagementStatus: EngagementStatus | 'any';
}

const DEFAULT_FILTERS: Filters = {
  recency: 'any',
  radiusKm: 'any',
  anchorStudio: 'coburg',
  artistId: 'any',
  designType: 'any',
  depositHistory: 'any',
  signal: 'any',
  engagementStatus: 'any',
};

function recencyMonthsCutoff(band: RecencyBand): [number, number] | null {
  switch (band) {
    case '0-30':
      return [0, 1];
    case '31-90':
      return [1, 3];
    case '91-180':
      return [3, 6];
    case '181-365':
      return [6, 12];
    case '1-2y':
      return [12, 24];
    case '2y+':
      return [24, 60];
    default:
      return null;
  }
}

function matches(filters: Filters, r: TailRecord): boolean {
  if (filters.designType === 'unfinished-sleeves') {
    if (!r.hasUnfinishedPiece) return false;
  } else if (filters.designType !== 'any') {
    if (r.designType !== filters.designType) return false;
  }
  if (filters.radiusKm !== 'any') {
    if (r.primaryStudioId !== filters.anchorStudio) return false;
    if (r.distanceFromNearestKm > parseInt(filters.radiusKm, 10)) return false;
  }
  if (filters.engagementStatus !== 'any' && r.engagementStatus !== filters.engagementStatus) {
    return false;
  }
  if (filters.recency !== 'any') {
    const range = recencyMonthsCutoff(filters.recency);
    if (range) {
      const months =
        (Date.now() - new Date(r.lastInteractionAt).getTime()) / (1000 * 60 * 60 * 24 * 30);
      if (months < range[0] || months >= range[1]) return false;
    }
  }
  if (filters.depositHistory !== 'any') {
    const everPaid = typeof r.lastDepositAud === 'number';
    if (filters.depositHistory === 'paid-ever' && !everPaid) return false;
    if (filters.depositHistory === 'never' && everPaid) return false;
    if (filters.depositHistory === 'multi' && !everPaid) return false;
  }
  if (filters.signal === 'future-readiness' && !r.futureSignal) return false;
  if (filters.signal === 'lapsed' && r.engagementStatus !== 'lapsed') return false;
  if (filters.signal === 'no-signal' && r.futureSignal) return false;
  return true;
}

const HEROIC_PRESET: Partial<Filters> = {
  designType: 'unfinished-sleeves',
  radiusKm: '50',
  anchorStudio: 'coburg',
};

export default function ReactivationEngine() {
  const tailRecords = useBrainStore((s) => s.tailRecords);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const campaigns = useBrainStore((s) => s.reactivationCampaigns);
  const activity = useBrainStore((s) => s.activityFeed);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [campaignLive, setCampaignLive] = useState(false);

  const segment = useMemo(() => tailRecords.filter((r) => matches(filters, r)), [filters, tailRecords]);
  const total = tailRecords.length;

  const campaign = Object.values(campaigns)[0];

  const recentOutcomes = activity
    .filter((a) => a.type === 'reactivation-warm-handoff')
    .slice(0, 6);

  return (
    <section className="px-8 py-8" data-testid="reactivation-engine">
      <header className="flex items-end justify-between max-w-7xl">
        <div>
          <p className="label-small-caps text-gold-bright">PRD §12.9</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Reactivation Engine</h1>
          <p className="font-serif text-cream-soft mt-3">
            The 50,000-record book becomes queryable. Segment, launch, watch warm hand-offs land.
          </p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <DashboardKPITile label="Total records" value={total.toLocaleString('en-AU')} />
          <DashboardKPITile
            label="Active segment"
            value={segment.length.toLocaleString('en-AU')}
            accent
          />
          {campaign ? (
            <>
              <DashboardKPITile label="Calls overnight" value={campaign.callsCompleted.toString()} />
              <DashboardKPITile
                label="Warm hand-offs"
                value={campaign.warmHandoffs.toString()}
                caption={`Cost ${aud(campaign.costSpentAud)}`}
              />
            </>
          ) : null}
        </div>
      </header>

      <div className="mt-6 grid grid-cols-12 gap-6 max-w-7xl">
        <aside className="col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="label-small-caps text-gold-bright">Segment</h3>
            <button
              type="button"
              data-testid="apply-heroic-preset"
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  ...HEROIC_PRESET,
                }))
              }
              className="btn-quiet text-[10px]"
            >
              Apply Coburg preset
            </button>
          </div>
          <div className="space-y-3 panel p-4">
            <FilterChip
              icon={<Calendar size={12} />}
              label="Recency"
              value={filters.recency}
              testid="filter-recency"
              options={[
                ['any', 'Any'],
                ['0-30', '0-30 days'],
                ['31-90', '31-90 days'],
                ['91-180', '91-180 days'],
                ['181-365', '181-365 days'],
                ['1-2y', '1-2 years'],
                ['2y+', '2+ years'],
              ]}
              onChange={(v) => setFilters((f) => ({ ...f, recency: v as RecencyBand }))}
            />
            <FilterChip
              icon={<MapPin size={12} />}
              label="Radius"
              value={filters.radiusKm}
              testid="filter-radius"
              options={[
                ['any', 'Any'],
                ['10', 'within 10km'],
                ['25', 'within 25km'],
                ['50', 'within 50km'],
                ['100', 'within 100km'],
              ]}
              onChange={(v) => setFilters((f) => ({ ...f, radiusKm: v as Radius }))}
            />
            <FilterChip
              icon={<MapPin size={12} />}
              label="Anchor studio"
              value={filters.anchorStudio}
              testid="filter-anchor"
              options={Object.values(studios).map((s) => [s.id, s.name] as [string, string])}
              onChange={(v) => setFilters((f) => ({ ...f, anchorStudio: v }))}
            />
            <FilterChip
              icon={<Brush size={12} />}
              label="Design"
              value={filters.designType}
              testid="filter-design"
              options={[
                ['any', 'Any'],
                ['unfinished-sleeves', 'Unfinished sleeves'],
                ['black-and-grey', 'Black-and-grey'],
                ['colour-realism', 'Colour realism'],
                ['fine-line', 'Fine-line'],
                ['traditional', 'Traditional'],
                ['sleeve', 'Sleeve'],
                ['half-sleeve', 'Half-sleeve'],
              ]}
              onChange={(v) => setFilters((f) => ({ ...f, designType: v as Filters['designType'] }))}
            />
            <FilterChip
              icon={<Sparkles size={12} />}
              label="Artist"
              value={filters.artistId}
              testid="filter-artist"
              options={[
                ['any', 'Any'],
                ...Object.values(artists).map((a) => [a.id, a.displayName] as [string, string]),
              ]}
              onChange={(v) => setFilters((f) => ({ ...f, artistId: v }))}
            />
            <FilterChip
              icon={<Wallet size={12} />}
              label="Deposit"
              value={filters.depositHistory}
              testid="filter-deposit"
              options={[
                ['any', 'Any'],
                ['paid-ever', 'Ever paid'],
                ['never', 'Never paid'],
                ['multi', 'Multiple deposits'],
                ['has-credit', 'Has credit on file'],
              ]}
              onChange={(v) =>
                setFilters((f) => ({ ...f, depositHistory: v as Filters['depositHistory'] }))
              }
            />
            <FilterChip
              icon={<Search size={12} />}
              label="Signal"
              value={filters.signal}
              testid="filter-signal"
              options={[
                ['any', 'Any'],
                ['future-readiness', 'Has future signal'],
                ['lapsed', 'Lapsed >6 months'],
                ['no-signal', 'No signal'],
              ]}
              onChange={(v) => setFilters((f) => ({ ...f, signal: v as Filters['signal'] }))}
            />

            <p className="font-inter text-[11px] text-gold-bright mt-3 uppercase tracking-widest">
              {segment.length.toLocaleString('en-AU')} of {total.toLocaleString('en-AU')} records
            </p>
          </div>
        </aside>

        <section className="col-span-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="label-small-caps text-gold-bright">Live campaign feed</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCampaignLive((v) => !v)}
                data-testid="campaign-toggle"
                className={clsx(
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs',
                  campaignLive
                    ? 'bg-blood/20 border border-blood/60 text-blood'
                    : 'bg-gold-bright text-ink-black',
                )}
              >
                {campaignLive ? (
                  <>
                    <PauseCircle size={12} /> Pause campaign
                  </>
                ) : (
                  <>
                    <PlayCircle size={12} /> Launch campaign
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => simulationApi.triggerEvent('aaron-singh-handoff')}
                className="btn-ghost text-xs"
                data-testid="trigger-aaron-singh"
              >
                <Phone size={12} /> Simulate next warm hand-off
              </button>
            </div>
          </div>
          <div className="panel p-4 min-h-[300px]">
            {recentOutcomes.length === 0 ? (
              <p className="font-serif text-cream-soft text-sm">
                No outcomes yet. Trigger Aaron Singh&apos;s hand-off to see the warm hand-off card.
              </p>
            ) : null}
            <ol className="space-y-2">
              {recentOutcomes.map((entry) => (
                <li
                  key={entry.id}
                  className="rounded-md border border-gold-bright/30 bg-gold/5 p-3"
                  data-testid={`reactivation-outcome-${entry.id}`}
                >
                  <p className="font-inter text-sm text-cream">{entry.body}</p>
                  <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                    {simTimeShort(entry.timestamp)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <aside className="col-span-3">
          <h3 className="label-small-caps text-gold-bright mb-2">Sample segment</h3>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mb-2">
            First 6 of {segment.length}
          </p>
          <ol className="space-y-2">
            {segment.slice(0, 6).map((r) => (
              <li key={r.id} className="panel-dark p-3 text-xs">
                <p className="font-inter text-cream">
                  {r.firstName} {r.lastName}
                </p>
                <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                  {r.suburb} · {r.designType} · {r.engagementStatus}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-6 panel p-4">
            <p className="label-small-caps mb-2">Cost preview</p>
            <p className="font-mono text-xs text-cream-soft">Per call</p>
            <p className="font-cinzel text-lg text-cream">$0.13</p>
            <p className="font-mono text-xs text-cream-soft mt-3">Cost-per-rebooking</p>
            <p className="font-cinzel text-lg text-gold-bright">
              {campaign ? aud(campaign.costSpentAud / Math.max(1, campaign.rebookings)) : '$2.10'}
            </p>
            <p className="font-serif text-[10px] text-cream-soft mt-2">
              vs current ad-cost-per-deposit benchmark $48.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}

interface FilterChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  options: Array<[string, string]>;
  testid?: string;
  onChange: (value: string) => void;
}

function FilterChip({ icon, label, value, options, testid, onChange }: FilterChipProps) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 label-small-caps mb-1">
        {icon}
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-xs text-cream focus:outline-none focus:border-gold-bright"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
